// When each city's incoming attacks land (Ben, Oct 14 2026). The city list only counts them, so while the city switcher
// is open this reads village/get for every city with something incoming (never the clear ones) — the same cache entry
// the city screens and the swipe switch use, so a city read in the last minute isn't asked for again. Three at a time,
// and again every minute while the switcher stays open.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { PlayerVillage, Village } from "@/api/types";
import { incomingCount, incomingOf, type IncomingBook } from "@/game/incoming";
import { fetchVillageFresh, keys } from "./queries";
import { serverNow } from "./clock";

const FRESH_MS = 60_000;
const AT_ONCE = 3;

export function useIncomingBook(wid: number, cities: PlayerVillage[], active = true): { book: IncomingBook; reading: number } {
  const qc = useQueryClient();
  const [tick, setTick] = useState(0);
  const [reading, setReading] = useState(0);
  const targets = useMemo(() => cities.filter((c) => incomingCount(c) > 0).map((c) => c.id), [cities]);
  const sig = targets.join(",");

  useEffect(() => {
    if (!active || !wid || !targets.length) return;
    let live = true;
    const run = async () => {
      const stale = targets.filter((id) => {
        const st = qc.getQueryState(keys.village(wid, id));
        return !st?.data || Date.now() - st.dataUpdatedAt > FRESH_MS;
      });
      if (!live) return;
      setReading(stale.length);
      let i = 0;
      const worker = async () => {
        while (live && i < stale.length) {
          const id = stale[i++];
          try {
            await fetchVillageFresh(qc, wid, id, FRESH_MS);
          } catch {
            /* a city that won't answer keeps its count without a time */
          }
          if (live) {
            setReading((n) => Math.max(0, n - 1));
            setTick((t) => t + 1);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(AT_ONCE, stale.length) }, worker));
    };
    run();
    const id = window.setInterval(run, FRESH_MS);
    return () => {
      live = false;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, wid, sig, qc]);

  const book = useMemo(() => {
    const b: IncomingBook = new Map();
    const now = serverNow();
    for (const id of targets) {
      const v = qc.getQueryData<Village>(keys.village(wid, id));
      if (v) b.set(id, incomingOf(v, now));
    }
    return b;
    // tick: a city was just read; cities: the active city's poll brought new counts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, wid, sig, tick, cities]);

  return { book, reading };
}
